(() => {
  const instances = new WeakMap();

  const money = cents => {
    const value = Number(cents || 0) / 100;
    return `PKR ${value.toLocaleString('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const quantityFor = (state, card) => state.quantities.get(card.dataset.id) || 0;
  const cardsFor = (state, group) => state.cards.filter(card => card.dataset.g === group);
  const groupTotal = (state, group) => cardsFor(state, group)
    .reduce((total, card) => total + quantityFor(state, card), 0);

  const trimGroup = (state, group, limit) => {
    let excess = groupTotal(state, group) - limit;
    if (excess <= 0) return false;
    [...cardsFor(state, group)].reverse().forEach(card => {
      if (excess <= 0) return;
      const current = quantityFor(state, card);
      const reduction = Math.min(current, excess);
      state.quantities.set(card.dataset.id, current - reduction);
      excess -= reduction;
    });
    return true;
  };

  const renderSummary = state => {
    const list = state.root.querySelector('[data-summary-items]');
    if (!list) return;
    list.replaceChildren();
    let selections = 0;
    state.cards.forEach(card => {
      const quantity = quantityFor(state, card);
      if (!quantity) return;
      selections += 1;
      const row = document.createElement('div');
      const image = card.querySelector('img');
      const title = card.querySelector('h3');
      if (image) {
        const thumbnail = document.createElement('img');
        thumbnail.src = image.currentSrc || image.src;
        thumbnail.alt = '';
        row.append(thumbnail);
      }
      const label = document.createElement('span');
      label.textContent = `${title ? title.textContent : ''} × ${quantity}`;
      row.append(label);
      list.append(row);
    });
    if (!selections) {
      const empty = document.createElement('p');
      empty.textContent = state.root.dataset.noneSelected;
      list.append(empty);
    }
  };

  const update = state => {
    const mainCount = groupTotal(state, 'm');
    const flowerCount = groupTotal(state, 'f');
    const mainReady = mainCount === state.target;
    const flowerReady = flowerCount === state.flowerTarget;
    const valid = mainReady && flowerReady && state.mainCards.length > 0;
    let mainPrice = 0;
    let flowerPrice = 0;

    state.cards.forEach(card => {
      const quantity = quantityFor(state, card);
      const price = quantity * Number(card.dataset.p || 0);
      if (card.dataset.g === 'f') flowerPrice += price;
      else mainPrice += price;
      card.classList.toggle('is-selected', quantity > 0);
      const quantityNode = card.querySelector('[data-q]');
      if (quantityNode) quantityNode.textContent = quantity;
      const group = card.dataset.g;
      const groupLimit = group === 'f' ? state.flowerTarget : state.target;
      const currentGroupTotal = group === 'f' ? flowerCount : mainCount;
      const locked = group === 'f' && !mainReady;
      const decrease = card.querySelector('[data-n="-1"]');
      const increase = card.querySelector('[data-n="1"]');
      if (decrease) decrease.disabled = state.submitting || locked || quantity === 0;
      if (increase) {
        increase.disabled = state.submitting || locked || currentGroupTotal >= groupLimit;
        increase.textContent = quantity > 0 ? '+' : state.root.dataset.addLabel;
        increase.setAttribute('aria-label', quantity > 0 ? state.root.dataset.increaseLabel : state.root.dataset.addLabel);
      }
    });

    state.root.querySelectorAll('[data-t]').forEach(button => {
      button.classList.toggle('is-active', Number(button.dataset.t) === state.target);
      button.disabled = state.submitting;
    });
    const flowerStep = state.root.querySelector('[data-flower-step]');
    if (flowerStep) {
      flowerStep.classList.toggle('is-locked', !mainReady);
      flowerStep.setAttribute('aria-disabled', String(!mainReady));
      flowerStep.querySelectorAll('[data-ft]').forEach(button => {
        button.classList.toggle('is-active', Number(button.dataset.ft) === state.flowerTarget);
        button.disabled = state.submitting || !mainReady;
      });
    }

    const count = state.root.querySelector('[data-count]');
    const flowerCounter = state.root.querySelector('[data-fcount]');
    if (count) count.textContent = `${mainCount} / ${state.target}`;
    if (flowerCounter) flowerCounter.textContent = `${flowerCount} / ${state.flowerTarget}`;
    const discountPercent = mainCount >= 5 ? 10 : mainCount >= 2 ? 5 : 0;
    const discountRate = 1 - discountPercent / 100;
    const subtotal = state.root.querySelector('[data-subtotal]');
    const discount = state.root.querySelector('[data-discount]');
    const total = state.root.querySelector('[data-total]');
    if (subtotal) subtotal.textContent = money(mainPrice + flowerPrice);
    if (discount) discount.textContent = `${discountPercent}%`;
    if (total) total.textContent = money(Math.round(mainPrice * discountRate + flowerPrice));

    const addButton = state.root.querySelector('[data-add]');
    if (addButton) {
      addButton.disabled = state.submitting || !valid;
      addButton.textContent = state.submitting ? state.root.dataset.addingLabel : state.root.dataset.addBundleLabel;
    }
    const message = state.root.querySelector('[data-msg]');
    if (message) {
      message.classList.toggle('bb-message--error', Boolean(state.errorMessage));
      message.textContent = state.errorMessage || (valid ? state.root.dataset.readyLabel : state.root.dataset.incompleteLabel);
    }

    let currentStep = 2;
    if (state.root.dataset.kind === 'vase') currentStep = mainReady ? (flowerReady ? 4 : 3) : 2;
    else currentStep = mainReady ? 3 : 2;
    state.root.querySelectorAll('[data-progress-step]').forEach(item => {
      const step = Number(item.dataset.progressStep);
      item.classList.toggle('is-active', step === currentStep);
      item.classList.toggle('is-complete', step < currentStep);
    });
    state.root.toggleAttribute('aria-busy', state.submitting);
    renderSummary(state);
  };

  const cartItems = state => {
    const bundleId = `BB-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const mainCount = groupTotal(state, 'm');
    const discount = mainCount >= 5 ? '10' : mainCount >= 2 ? '5' : '0';
    return state.cards.flatMap(card => {
      const quantity = quantityFor(state, card);
      if (!quantity) return [];
      return [{
        id: Number(card.dataset.id),
        quantity,
        properties: {
          _bundle_id: bundleId,
          _bundle_type: state.root.dataset.kind,
          _bundle_size: state.target,
          _bundle_role: card.dataset.g,
          _bundle_discount: discount,
        },
      }];
    });
  };

  const addBundle = async state => {
    if (state.submitting) return;
    state.errorMessage = '';
    if (groupTotal(state, 'm') !== state.target || groupTotal(state, 'f') !== state.flowerTarget) {
      state.errorMessage = 'Please select the exact quantities shown before adding your bundle.';
      update(state);
      return;
    }
    const items = cartItems(state);
    if (!items.length) return;
    state.submitting = true;
    update(state);
    const controller = new AbortController();
    state.controller = controller;
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    const rootUrl = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || payload.message || 'Cart request failed');
      window.location.assign(`${rootUrl}cart`);
    } catch (error) {
      state.errorMessage = error?.name === 'AbortError'
        ? 'The connection timed out. Please try adding your bundle again.'
        : 'We could not add the bundle to your cart. Please try again.';
    } finally {
      window.clearTimeout(timeout);
      state.controller = null;
      state.submitting = false;
      update(state);
    }
  };

  const handleClick = (state, event) => {
    const button = event.target.closest('button');
    if (!button || !state.root.contains(button) || button.disabled || state.submitting) return;
    state.errorMessage = '';
    if (button.hasAttribute('data-add')) {
      addBundle(state);
      return;
    }
    if (button.dataset.t) {
      state.target = Number(button.dataset.t);
      if (trimGroup(state, 'm', state.target)) state.errorMessage = 'Bundle size updated. Extra selections were removed.';
      if (groupTotal(state, 'm') !== state.target) {
        state.flowerTarget = 0;
        trimGroup(state, 'f', 0);
      }
      update(state);
      return;
    }
    if (button.dataset.ft != null) {
      if (groupTotal(state, 'm') !== state.target) return;
      state.flowerTarget = Number(button.dataset.ft);
      if (trimGroup(state, 'f', state.flowerTarget)) state.errorMessage = 'Flower quantity updated. Extra selections were removed.';
      update(state);
      return;
    }
    const card = button.closest('.bb-card');
    if (!card || !button.dataset.n) return;
    const group = card.dataset.g;
    const limit = group === 'f' ? state.flowerTarget : state.target;
    if (group === 'f' && groupTotal(state, 'm') !== state.target) return;
    const delta = Number(button.dataset.n);
    const current = quantityFor(state, card);
    if (delta > 0 && groupTotal(state, group) >= limit) {
      state.errorMessage = 'You have selected the maximum quantity for this bundle.';
      update(state);
      return;
    }
    state.quantities.set(card.dataset.id, Math.max(0, current + delta));
    update(state);
  };

  const init = root => {
    const existing = instances.get(root);
    if (existing) {
      existing.submitting = false;
      existing.controller?.abort();
      existing.controller = null;
      update(existing);
      return;
    }
    const cards = [...root.querySelectorAll('.bb-card')];
    const state = {
      root,
      cards,
      mainCards: cards.filter(card => card.dataset.g === 'm'),
      quantities: new Map(cards.map(card => [card.dataset.id, 0])),
      target: 2,
      flowerTarget: 0,
      submitting: false,
      controller: null,
      errorMessage: '',
    };
    instances.set(root, state);
    root.addEventListener('click', event => handleClick(state, event));
    if (!state.mainCards.length) state.errorMessage = 'Products are temporarily unavailable. Please refresh this page and try again.';
    update(state);
  };

  const initAll = scope => {
    if (scope?.matches?.('[data-bb]')) init(scope);
    scope?.querySelectorAll?.('[data-bb]').forEach(init);
  };
  const boot = () => initAll(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  window.addEventListener('pageshow', boot);
})();
